import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.76, 0, 0.24, 1];

// Masked line-by-line reveal for headings. `lines` is an array of strings/nodes.
// Strings containing `*text*` will render the wrapped words as italic terracotta.
const renderInline = (str) => {
  if (typeof str !== "string") return str;
  const parts = str.split(/(\*[^*]+\*)/g).filter(Boolean);
  return parts.map((p, i) =>
    p.startsWith("*") && p.endsWith("*") ? (
      <span key={i} className="italic text-alch-terracotta">{p.slice(1, -1)}</span>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
};

export const MaskedLines = ({ lines, className = "", delay = 0, stagger = 0.12, as = "h1" }) => {
  const Tag = motion[as] || motion.h1;
  return (
    <Tag className={className} aria-label={typeof lines[0] === "string" ? lines.join(" ") : undefined}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, ease: EASE, delay: delay + i * stagger }}
          >
            {renderInline(line)}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

// Scroll reveal wrapper
export const Reveal = ({ children, className = "", delay = 0, y = 40, once = true }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
};

// Image that unmasks (clip) + subtle scale on scroll into view
export const RevealImage = ({ src, alt, className = "", imgClassName = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        className={`h-full w-full object-cover ${imgClassName}`}
        initial={{ scale: 1.25, opacity: 0.4 }}
        animate={inView ? { scale: 1, opacity: 1 } : {}}
        transition={{ duration: 1.1, ease: EASE, delay }}
      />
    </div>
  );
};

// Animated number counter
export const Counter = ({ value, suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.floor(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return (
    <span ref={ref} className={className}>
      {display.toLocaleString()}{suffix}
    </span>
  );
};

// Seamless marquee
export const Marquee = ({ items, className = "" }) => (
  <div className={`relative flex overflow-hidden ${className}`}>
    <div className="flex animate-marquee whitespace-nowrap">
      {[...items, ...items].map((it, i) => (
        <span key={i} className="mx-8 flex items-center gap-8">
          <span>{it}</span>
          <span className="text-alch-terracotta">✦</span>
        </span>
      ))}
    </div>
  </div>
);

// Magnetic-ish button hover via framer-motion
export { motion, EASE };
